import { useEffect, useRef, useState } from 'react';

const BAR_SIZE = 16;
const HIT_AREA = 9;

const COLORS = {
  barBorder: '#E4E2EA',
  guide: '#8B5CF6',
  guideGlow: 'rgba(139, 92, 246, 0.35)'
};

type Orientation = 'horizontal' | 'vertical';
type Guide = { pos: number };

export const RulerGuides: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<Orientation | null>(null);
  const [hGuide, setHGuide] = useState<Guide | null>(null);
  const [vGuide, setVGuide] = useState<Guide | null>(null);
  const [hovering, setHovering] = useState<Orientation | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = draggingRef.current;
      const container = containerRef.current;
      if (!drag || !container) return;
      const rect = container.getBoundingClientRect();
      const pos = drag === 'horizontal' ? e.clientY - rect.top : e.clientX - rect.left;
      if (drag === 'horizontal') setHGuide({ pos });
      else setVGuide({ pos });
    };

    const onUp = () => {
      const drag = draggingRef.current;
      if (!drag) return;
      draggingRef.current = null;
      const setter = drag === 'horizontal' ? setHGuide : setVGuide;
      setter(g => (g && g.pos < BAR_SIZE ? null : g));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startFromBar = (orientation: Orientation) => (e: React.MouseEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const pos = orientation === 'horizontal' ? e.clientY - rect.top : e.clientX - rect.left;
    if (orientation === 'horizontal') setHGuide({ pos });
    else setVGuide({ pos });
    draggingRef.current = orientation;
  };

  const startFromGuide = (orientation: Orientation) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = orientation;
  };

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
      {/* Top bar */}
      <div
        onMouseDown={startFromBar('horizontal')}
        onMouseEnter={() => setHovering('horizontal')}
        onMouseLeave={() => setHovering(h => (h === 'horizontal' ? null : h))}
        style={{
          position: 'absolute',
          top: 0,
          left: BAR_SIZE,
          right: 0,
          height: BAR_SIZE,
          borderBottom: `1px solid ${COLORS.barBorder}`,
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.6)',
          cursor: 'ns-resize',
          pointerEvents: 'auto',
          zIndex: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s ease'
        }}
      >
        <div
          style={{
            width: 18,
            height: 2,
            borderRadius: 1,
            background: hovering === 'horizontal' ? COLORS.guide : '#B8BCC4',
            opacity: hovering === 'horizontal' ? 0.9 : 0.5,
            transition: 'all 0.15s ease'
          }}
        />
      </div>

      {/* Left bar */}
      <div
        onMouseDown={startFromBar('vertical')}
        onMouseEnter={() => setHovering('vertical')}
        onMouseLeave={() => setHovering(h => (h === 'vertical' ? null : h))}
        style={{
          position: 'absolute',
          top: BAR_SIZE,
          left: 0,
          bottom: 0,
          width: BAR_SIZE,
          borderRight: `1px solid ${COLORS.barBorder}`,
          boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.6)',
          cursor: 'ew-resize',
          pointerEvents: 'auto',
          zIndex: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s ease'
        }}
      >
        <div
          style={{
            width: 2,
            height: 18,
            borderRadius: 1,
            background: hovering === 'vertical' ? COLORS.guide : '#B8BCC4',
            opacity: hovering === 'vertical' ? 0.9 : 0.5,
            transition: 'all 0.15s ease'
          }}
        />
      </div>

      {/* Corner */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: BAR_SIZE,
          height: BAR_SIZE,
          borderRight: `1px solid ${COLORS.barBorder}`,
          borderBottom: `1px solid ${COLORS.barBorder}`,
          pointerEvents: 'auto',
          zIndex: 17
        }}
      />

      {/* Horizontal guide */}
      {hGuide && (
        <div
          onMouseDown={startFromGuide('horizontal')}
          style={{
            position: 'absolute',
            top: hGuide.pos - HIT_AREA / 2,
            left: 0,
            right: 0,
            height: HIT_AREA,
            display: 'flex',
            alignItems: 'center',
            cursor: 'ns-resize',
            pointerEvents: 'auto',
            zIndex: 18
          }}
        >
          <div
            style={{
              width: '100%',
              height: 1,
              background: COLORS.guide,
              boxShadow: `0 0 0 0.5px ${COLORS.guideGlow}, 0 1px 2px rgba(0,0,0,0.08)`
            }}
          />
        </div>
      )}

      {/* Vertical guide */}
      {vGuide && (
        <div
          onMouseDown={startFromGuide('vertical')}
          style={{
            position: 'absolute',
            left: vGuide.pos - HIT_AREA / 2,
            top: 0,
            bottom: 0,
            width: HIT_AREA,
            display: 'flex',
            justifyContent: 'center',
            cursor: 'ew-resize',
            pointerEvents: 'auto',
            zIndex: 18
          }}
        >
          <div
            style={{
              height: '100%',
              width: 1,
              background: COLORS.guide,
              boxShadow: `0 0 0 0.5px ${COLORS.guideGlow}, 1px 0 2px rgba(0,0,0,0.08)`
            }}
          />
        </div>
      )}
    </div>
  );
};
